import { uuidv7 } from "uuidv7"

import type { AgentStatusTurnPart } from "~/models/polyschema/AgentStatusTurnPart"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { InterfaceConversation } from "~/classes/conversation/InterfaceConversation"
import { DatagramErrorType, Property, PropertySchema, PropertyStatus } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { sendConversationCreateRequests } from "~/classes/handlers/conversation/create"
import { sendConversationDeleteRequests } from "~/classes/handlers/conversation/delete"
import { sendSelfConversationsRequest } from "~/classes/handlers/user/conversations"

// TODO
// - [ ] Handle the `onAfterNavigatedToConversation` event

interface State {
  all: Record<string, InterfaceConversation>
  /**
   * current / latest conversation id, if any. Will get set from the
   * user's settings and updating this should trigger updates to the
   * settings.
   */
  current?: string
  /**
   * Whether we have loaded the user's conversations yet.
   */
  initialized?: boolean

  subscriptions: Record<string, boolean>
}

class Conversations extends GlobalComposable<State> {
  override defaultState = () => ({
    all: {},
    subscriptions: {},
  })

  override onCreate = () => {
    const { userInterfaceSettings } = useSelf()
    const { addGenericEntityEventListener, addInterfaceEventListener } = useEvents()
    addGenericEntityEventListener("conversationProperties", this._onConversationProperties)
    addInterfaceEventListener("selfConversationsResponse", this.onSelfConversationsResponse)
    addInterfaceEventListener("conversationPropertiesResponse", this._onConversationPropertyResponse)

    watchImmediate(userInterfaceSettings, (settings) => {
      this.state.value.current = settings.latestConversationId
    })

    // TODO: watch when the current conversation changes and update properties
  }

  override onDestroy = () => {
    const { removeGenericEntityEventListener, removeInterfaceEventListener } = useEvents()
    removeGenericEntityEventListener("conversationProperties", this._onConversationProperties)
    removeInterfaceEventListener("selfConversationsResponse", this.onSelfConversationsResponse)
    removeInterfaceEventListener("conversationPropertiesResponse", this._onConversationPropertyResponse)
  }

  /**
   * Migrate to some useConversation / useAgent composable
   */
  requestSelfConversations = () => {
    const request: SelfConversationsRequestDatagram = {
      requestId: generateWorkerRequestId(),
      offline: false,
      selection: InterfaceSelection.minimumSelfConversationSelection().inner(),
    }
    sendSelfConversationsRequest(request)
    return request
  }

  onSelfConversationsResponse = async (response: SelfConversationsResponseDatagram) => {
    const { conversations, error, offline } = response

    if (error && !offline) {
      // for non-offline errors, then we need to log out
      this.error("Fatal error retrieving conversations, user has no agent :(", error)
      return
    }

    if (error || conversations === undefined)
      return this.info("Cached self conversations errored (likely first time logging in)")

    // add all conversations to the state and remove all that are not in the response
    const allConversations = new Set(conversations.map(c => c.conversationId))
    for (const conversation of conversations)
      this.addOrUpdateConversation(InterfaceConversation.fromEvent(conversation))
    for (const conversationId of Object.keys(this.state.value.all))
      if (!allConversations.has(conversationId))
        this.removeConversation(conversationId)
    this.state.value.initialized = true
  }

  waitForConversationsToInitialize = async () => {
    if (this.state.value.initialized)
      return

    const { resolve, promise } = Promise.withResolvers<void>()
    const unwatch = watch(() => this.state.value.initialized, (state) => {
      if (state)
        resolve()
    })
    await promise
    unwatch()
    return
  }

  conversation = (conversationId: string) => this.state.value.all[conversationId]

  currentConversationId = computed(() => this.state.value.current || undefined)
  currentConversation = computed(() => this.currentConversationId.value
    ? this.state.value.all[this.currentConversationId.value]
    : undefined,
  )

  /**
   * We received conversation properties. This could mean:
   * 1. We or someone else changed the name
   * 2. The members changed, including us being removed and unsubscribed
   * 3. A message was sent in the conversation by us or someone else
   * 4. The conversation was closed / paused by us or someone else
   * 5. We fetched properties
   */
  _onConversationProperties = (conversation: ConversationEvent) => {
    const { state: myself } = useSelf()

    const iConversation = InterfaceConversation.fromEvent(conversation)

    // if we have been removed, remove the conversation and switch to the latest one
    if (myself.value.user && iConversation.isUserRemoved(myself.value.user.userId))
      return this.removeConversationAndSwitchToLatest(iConversation.conversationId)

    this.addOrUpdateConversation(iConversation)
  }

  /**
   * This handler is only to respond to errors in property requests. We may receive
   * them in a few instances:
   * - We try to navigate to a deleted conversation
   * - We refresh the page on a deleted conversation or open in our sidebar
   * - Someone else removes us from the conversation
   */
  _onConversationPropertyResponse = (response: ConversationPropertiesResponseDatagram) => {
    const { conversationId, error, offline } = response
    if (error && !offline) {
      this.error("Error retrieving conversation properties", error)
      // if we received an error, we should remove the conversation and switch to
      // another one, as this conversation might be deleted
      this.removeConversationAndSwitchToLatest(conversationId)
    }
  }

  /**
   * Transcript where subsequent agent fragments and status parts are merged in since
   * they are initially streamed. Iterates over the transcript, placing agent turns in
   * an array then pushing them as fragments before a user turn is pushed
   */
  _mergedTranscript = (properties: ConversationProperty[]): ConversationTurn[] => {
    const turnWise: ConversationTurn[] = []
    let fragments: AgentOutputTurnFragment[] = []
    let parts: AgentStatusTurnPart[] = []

    const flushFragmentsIfNeeded = () => {
      if (len(fragments) === 0)
        return
      turnWise.push({ agent: { output: { fragments } } })
      fragments = []
    }
    const flushPartsIfNeeded = () => {
      if (len(parts) === 0)
        return
      turnWise.push({ agent: { status: { parts } } })
      parts = []
    }

    for (const property of properties) {
      if (property.value.type !== PropertySchema.ConversationTurnValue)
        throw new Error("Must be a turn")
      if ("agent" in property.value.turn && "output" in property.value.turn.agent) {
        flushPartsIfNeeded()
        fragments.push(...property.value.turn.agent.output.fragments)
      }
      else if ("agent" in property.value.turn && "status" in property.value.turn.agent) {
        flushFragmentsIfNeeded()
        parts.push(...property.value.turn.agent.status.parts)
      }
      else {
        flushPartsIfNeeded()
        flushFragmentsIfNeeded()
        turnWise.push(property.value.turn)
      }
    }
    flushPartsIfNeeded()
    flushFragmentsIfNeeded()

    return turnWise
  }

  currentTranscript = computed(() => this._mergedTranscript(this.currentConversation.value?.turnProperties() || []))

  addOrUpdateConversation = (conversation: InterfaceConversation) => {
    let existing = this.state.value.all[conversation.conversationId]
    if (isDefined(existing))
      existing = existing.mergeWithNewer(conversation)
    else
      existing = conversation
    this.state.value.all[existing.conversationId] = existing
  }

  removeConversation = (conversationId: string) => {
    delete this.state.value.all[conversationId]
    if (this.state.value.current === conversationId)
      delete this.state.value.current
  }

  /**
   * Conversations ordered by their updated date
   */
  conversationsByUpdatedAt = computed(() => {
    return Object.values(this.state.value.all)
      .sort((a, b) => b.lastUpdated().getTime() - a.lastUpdated().getTime())
  })

  latestConversation = computed(() => first(this.conversationsByUpdatedAt.value))

  createNewConversation = async () => {
    const { isAuthenticated, state: myself } = useSelf()
    if (!isAuthenticated.value || !myself.value.user)
      throw new Error("Cannot create a conversation without being signed in")
    const userId = myself.value.user.userId

    const preHash = InterfaceConversation.clientPreHash()
    const timestamp = uuidv7()
    const conversationId = await InterfaceConversation.idFromPrehash(preHash)
    const conversation = InterfaceConversation
      .blank(conversationId)
      .withSelfOwner(userId, timestamp)
      .withCreatedAt(userId, timestamp)

    sendConversationCreateRequests([{
      requestId: generateWorkerRequestId(),
      offline: false,
      conversation: conversation.inner(),
      preHash,
    }])

    this.addOrUpdateConversation(conversation)
    return conversation
  }

  /**
   * If the latest conversation is empty, we go to it to save us from creating a new one.
   * If it isn't empty, then we must create a new one, so that we can give the user an
   * empty conversation to start with.
   */
  getLatestIfEmptyOrCreateConversation = async () => {
    if (this.latestConversation.value?.isEmpty())
      return this.latestConversation.value

    const newConversation = await this.createNewConversation()
    return newConversation
  }

  /**
   * When selecting a conversation from the chats menu or opening the panel
   * to the default, call this method to set the conversation state to the
   * correct values. It will check if a current conversation exists. If not,
   */
  setupCurrentConversation = async (conversation?: InterfaceConversation) => {
    const { onSwitchedToConversation } = useAgent()

    if (conversation && conversation.conversationId === this.state.value.current)
      return console.info("Already on the conversation", conversation.conversationId)
    else if (!conversation && this.state.value.current) {
      // NOTE: We don't return here since it's possible that we are opening
      // this conversation at the start of the app, so we still need to initialize
      // it
      console.info("Opening panel to existing conversation", this.state.value.current)
      conversation = this.state.value.all[this.state.value.current]!
    }
    
    if (!conversation)
      conversation = await this.getLatestIfEmptyOrCreateConversation()

    this.state.value.current = conversation.conversationId

    if (!this.state.value.subscriptions[conversation.conversationId])
      this.subscribeToConversation(conversation)

    onSwitchedToConversation(conversation)
  }

  /**
   * If there is no latest conversation we have to create a new one.
   */
  deleteConversationAndSwitchToLatest = async (conversationId: string) => {
    const { state: myself } = useSelf()
    if (!myself.value.user)
      throw new Error("Cannot delete a conversation without being signed in")

    const conversation = InterfaceConversation.blank(conversationId)
    conversation.addProperty({
      conversationId,
      propertyId: Property.CONVERSATION_IS_DELETED,
      value: { type: PropertySchema.BoolValue, bool: true },
      timestamp: uuidv7(),
      version: 0,
      schema: PropertySchema.BoolValue,
      author: myself.value.user.userId,
      status: PropertyStatus.StatusPending,
    })

    sendConversationDeleteRequests([{
      requestId: generateWorkerRequestId(),
      offline: false,
      conversation: conversation.inner(),
    }])
    return this.removeConversationAndSwitchToLatest(conversationId)
  }

  /**
   * Does not send a deletion request, useful for removing stale/cached chats or
   * responding to a deletion message.
   */
  removeConversationAndSwitchToLatest = async (conversationId: string) => {
    const { onSwitchedToConversation } = useAgent()
    this.removeConversation(conversationId)
    let nextConversation = this.latestConversation.value
    if (!nextConversation)
      nextConversation = await this.createNewConversation()
    this.state.value.current = nextConversation.conversationId
    onSwitchedToConversation(nextConversation)
    return nextConversation
  }

  matchingConversations = async (query: string): Promise<InterfaceConversation[]> => {
    const { cacheWorker } = useCache()

    let conversationIds: string[] = []
    if (isDesktop()) {
      const { findMatchingConversations } = useTauri()
      conversationIds = await findMatchingConversations(query)
    }
    else {
      conversationIds = await cacheWorker().findMatchingConversations(query)
    }

    return conversationIds
      .map(id => this.state.value.all[id])
      .filter(c => isDefined(c))
  }

  subscribeToConversation = async (conversation: InterfaceConversation) => {
    if (this.state.value.subscriptions[conversation.conversationId])
      return console.debug("Already subscribed to conversation", conversation.conversationId)

    const result = await conversation.subscribe()
    if (isDefined(result.error)) {
      if (enumEq(result.error.type, DatagramErrorType.IgnoredDuplicateAction)) {
        this.state.value.subscriptions[conversation.conversationId] = true
        return console.warn("Already subscribed with that subscription id", conversation.conversationId)
      }
      this.error("Error subscribing to conversation", result.error)
      return
    }

    this.state.value.subscriptions[conversation.conversationId] = true
  }
}

const conversations = new Conversations("conversations", theme.colors.amber.hex)
export default function () {
  return conversations.use()
}
